<template>
  <div
    id="screenshotArea"
    class="absolute flex h-full w-full justify-center md:w-[60%]"
  >
    <div
      class="absolute bottom-[35vh] flex h-[40%] w-full flex-col items-center justify-center gap-[10vh] rounded-lg p-2 md:bottom-[20vh] md:h-[53%] md:w-[80%]"
    >
      <img
        class="absolute mb-[-4vh] h-[55vh] w-[110vw] scale-[1.2] md:mb-[12vh] md:mt-[25vh] md:h-[75vh] md:w-[50vw] md:scale-100"
        src="../assets/pitch.svg"
      />

      <div class="absolute z-10 h-[90%] w-[70%] bg-opacity-90"></div>

      <PlayerCard
        title="Goalkeeper"
        :players="[cardList[0]]"
        @selectCard="selectCard"
        :selectedPlayer="selectedPlayer"
        :captainId="captainId"
        :locale="locale"
      />
      <PlayerCard
        v-for="(position, index) in positions"
        :key="index"
        :players="position.players"
        @selectCard="selectCard"
        :selectedPlayer="selectedPlayer"
        :formation="formation"
        :captainId="captainId"
        :locale="locale"
      />

      <div
        v-if="isModalOpen"
        class="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50"
      ></div>

      <ModalPopUp
        @close="toggleModal"
        class="fixed transition-transform duration-700 ease-in-out"
        :class="{
          'translate-y-0 opacity-100': isModalOpen,
          'translate-y-full opacity-0': !isModalOpen,
        }"
      >
        <PlayerSearch
          @addPlayerToTeam="addPlayerToTeam"
          @addCaptain="addCaptain"
          @updateSelectedPlayer="handleSelectedPlayer"
          :cardPosition="cardPosition"
          :cardIndex="cardIndex"
          :cardList="cardList"
          :captainId="captainId"
        />
      </ModalPopUp>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps } from "vue";
import PlayerSearch from "./PlayerSearch.vue";
import PlayerCard from "./PlayerCard.vue";
import ModalPopUp from "./ModalPopUp.vue";
import { useI18n } from "vue-i18n";
import positionsData from "../data/positionsList.json";

const { locale } = useI18n();

const props = defineProps({
  formation: String,
});

const cardList = ref(
  Array.from({ length: 11 }, (_, i) => ({
    playerIndex: i,
    playerName: "",
    playerImage: "",
    playerId: "",
    playerCaps: null,
    playerWorldCups: "",
    position: i === 0 ? (locale.value === "en" ? "GK" :  "حارس المرمى" ) : "",
    positionCategory: i === 0 ? "Goalkeeper" : "",
    captain: false,
  }))
);

const isModalOpen = ref(false);
const selectedPlayer = ref(null);
const cardIndex = ref(null);
const cardPosition = ref("");
const captainId = ref(0);


const positions = computed(() => {
  const formationNumbers = props.formation.split("").map(Number).slice(0, 3);
  const players = [];
  let start = 1;

  formationNumbers.forEach((formationNumber) => {
    players.push({
      players: cardList.value.slice(start, start + formationNumber),
    });
    start += formationNumber;
  });

  if (locale.value === "en") {
    assignPositions(players, positionsData.en);
  } else {
    assignPositions(players, positionsData.ar);
  }

  return players;
});

function assignPositions(players, positionMapping) {
  assignPositionsByCategory(
    players,
    positionMapping.defenders,
    "Defender",
    calcDefendersPositions
  );
  assignPositionsByCategory(
    players,
    positionMapping.midfielders,
    "Midfielder",
    calcMidfieldersPositions
  );
  assignPositionsByCategory(
    players,
    positionMapping.attackers,
    "Attacker",
    calcAttackersPositions
  );
}

function assignPositionsByCategory(players, position, category, calcFunction) {
  const group = {
    Defender: players[0],
    Midfielder: players[1],
    Attacker: players[2],
  }[category];

  const count = group.players.length;
  group.players.forEach((player, index) => {
    player.position = calcFunction(count, index, position);
    player.positionCategory = category;
  });
}

const calcDefendersPositions = (count, index, position) => {
  if (count === 3) return position[0];
  if (count === 4)
    return index === 0 ? position[1] : index === 3 ? position[2] : position[0];
  if (count === 5) {
    if (index === 0) return position[3];
    if (index === 1 || index === 2 || index === 3) return position[0];
    if (index === 4) return position[4];
  }
};

const calcMidfieldersPositions = (count, index, position) => {
  if (count === 3 || count === 2) return position[0];
  if (count === 4) {
    if (index === 0) return position[1];
    if (index === 1) return position[0];
    if (index === 2) return position[0];
    if (index === 3) return position[2];
  }
  if (count === 5) {
    if (index === 0) return position[1];
    if (index === 1) return position[0];
    if (index === 2) return position[0];
    if (index === 3) return position[0];
    if (index === 4) return position[2];
  }
};

const calcAttackersPositions = (count, index, position) => {
  if (count === 1 || count === 2) return position[0];
  return index === 0 ? position[1] : index === 2 ? position[2] : position[0];
};

const selectCard = (player) => {
  cardPosition.value = player?.position;
  cardIndex.value = player?.playerIndex;
  toggleModal();
};

const toggleModal = () => {
  isModalOpen.value = !isModalOpen.value;
};

const handleSelectedPlayer = (newSelectedPlayer) => {
  selectedPlayer.value = newSelectedPlayer;
};

const addPlayerToTeam = (player) => {
  cardList.value[cardIndex.value].playerName = player.Name;
  cardList.value[cardIndex.value].playerId = player.id;
  cardList.value[cardIndex.value].playerImage = player.image;
  cardList.value[cardIndex.value].playerCaps = player.Caps;
  cardList.value[cardIndex.value].playerWorldCups = player.WorldCups;
  toggleModal();
  selectedPlayer.value = null;
};

const addCaptain = (player) => {
  captainId.value = player;
};
</script>

<style scoped>
body {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #4caf50;
  margin: 0;
}

.football-pitch {
  position: absolute;
  height: 80vh;
  border: 3px solid rgba(255, 255, 255);
  border-radius: 20px;
  transform: perspective(1400px) rotateX(30deg);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.center-circle {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 20%; /* Proportional to pitch size */
  height: 11vh;
  border: 3px solid rgba(255, 255, 255);
  border-radius: 50%;
}

.half-line {
  position: absolute;
  width: 3px;
  height: 100%;
  background-color: rgba(255, 255, 255);
  left: 50%;
  top: 0;
  transform: translateX(-50%);
}
</style>
